import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Checkbox,
  Group,
  LoadingOverlay,
  Modal,
  ScrollArea,
  Select,
  Table,
  Textarea,
  Tooltip,
} from '@mantine/core'
import { IconCheck, IconExternalLink } from '@tabler/icons-react'
import {
  ZOCDOC_CANCELLATION_REASON_TYPES,
  ZOCDOC_REASON_TYPES_NEEDING_TEXT,
  type ZocdocCancellationReasonType,
} from '@behuman/core-contract'
import { USDateInput } from '@/components/ui/USDateInput'
import { FIELD_CLASSES, MODAL_CONTENT } from '@/components/ui/formModal.tokens'
import { cn } from '@/utils/cn'
import { useClient, type OrpcClient } from '@/hooks/useClient'
import { useMiddlewareClient } from '@/hooks/useMiddlewareClient'
import { CreateUpdatePatientModal } from '@/components/ui/CreateUpdatePatientModal'
import { AdminPanel } from './AdminPanel'
import { AppointmentDetailsModal } from './AppointmentDetailsModal'
import { PatientCandidatesModal } from './PatientCandidatesModal'
import { canCreateChartFrom, prefillFromZocdoc } from './patientPrefill'
import { useZocdocPatientLink } from './useZocdocPatientLink'
import { RescheduleSteps } from './RescheduleSteps'
import { rescheduleNote } from './rescheduleOutcome'
import { thisWeek } from './dateRange'
import { formatDob, formatZocdocStart, splitZocdocStart } from './zocdocFormat'
import {
  HINT,
  ID_CHIP,
  PRIMARY_BTN,
  SECONDARY_BTN,
  SUBTLE_BTN,
  TABLE_HEAD,
  TABLE_ROW,
} from './tokens'

type MiddlewareClient = ReturnType<typeof useMiddlewareClient>
type AppointmentsResult = Awaited<ReturnType<OrpcClient['zocdocFetchAppointments']>>
type Appointment = NonNullable<AppointmentsResult['appointments']>[number]

/**
 * Zocdoc's own last page index, zero-based — mirrored from the contract's `page.max(10)`, which
 * mirrors Zocdoc. Past it the request is refused, so the card stops offering Next rather than
 * walking the operator into a validation error.
 */
const MAX_ZOCDOC_PAGE = 10

/**
 * Status tones. Anything unrecognised stays neutral rather than being guessed at — Zocdoc's
 * published field list names no status property, so the vocabulary is not fully known.
 */
const STATUS_CHIP: Record<string, string> = {
  confirmed: 'bg-[#DCFCE7] text-[#15803D]',
  rescheduled: 'bg-[#DCFCE7] text-[#15803D]',
  pending_booking: 'bg-[#FEF3C7] text-[#B45309]',
  pending_reschedule: 'bg-[#FEF3C7] text-[#B45309]',
  arrived: 'bg-[#DBEAFE] text-[#1D4ED8]',
  no_show: 'bg-[#FFEDD5] text-[#C2410C]',
  cancelled: 'bg-[#FEE2E2] text-[#DC2626]',
}

/**
 * Sends one action and turns its outcome into either a failure line or a note.
 *
 * Outside the component because it is pure plumbing over four differently-shaped responses, and
 * keeping the vocabularies together is what makes it obvious that every refusal is surfaced rather
 * than swallowed.
 */
/**
 * What Zocdoc's own row should read once an action lands, so a write it accepted but has not yet
 * applied can be told apart from one that changed nothing.
 */
const EXPECTED_STATUS: Record<SheetActionKey, string> = {
  confirm: 'confirmed',
  arrived: 'arrived',
  no_show: 'no_show',
  cancel: 'cancelled',
}

/**
 * Appended when the verifying read still shows the old status: the write succeeded, so this is not
 * an error, but the table is about to redraw looking untouched and the operator deserves to know
 * why rather than concluding the button is broken.
 */
const lagNote = (action: SheetActionKey, observedStatus?: string) =>
  observedStatus && observedStatus !== EXPECTED_STATUS[action]
    ? ` Zocdoc accepted it but still reports the appointment as “${observedStatus.replace(/_/g, ' ')}” — its own record can take a moment to catch up, so Refresh again shortly.`
    : ''

/**
 * Cancels the beHuman visit through **the calendar's own path** — `DELETE /appointment/{id}`, the
 * same endpoint `Schedule/service.ts` calls — so the billing-ledger guard, the slot release and the
 * hard delete all behave exactly as they do there. Nothing about cancellation is reimplemented
 * here; only the reporting is, because a 409 from the ledger has to be readable on this screen
 * rather than vanish into a toast.
 */
async function cancelInBehuman(
  middleware: MiddlewareClient,
  encounterId: string,
): Promise<{ ok: boolean; message?: string }> {
  const result = (await middleware.delete('/appointment/{encounterId}', { encounterId })) as {
    success?: boolean
    message?: string
  }
  return { ok: Boolean(result?.success), ...(result?.message && { message: result.message }) }
}

async function sendAction(
  client: OrpcClient,
  middleware: MiddlewareClient,
  action: SheetActionKey,
  target: ActionTarget,
  reasonType: ZocdocCancellationReasonType | null,
  reasonText: string,
  alsoCancelBehuman: boolean,
): Promise<{ failure?: string; note?: string }> {
  const zocdocAppointmentId = target.zocdocAppointmentId

  if (action === 'confirm') {
    const result = await client.zocdocConfirmAppointment({ zocdocAppointmentId })
    if (result.status === 'refused' && result.refusal) {
      return { failure: `Not confirmed — ${result.refusal.detail}.` }
    }
    return {
      note:
        result.status === 'already-confirmed'
          ? 'Zocdoc already had this appointment confirmed, so nothing was sent.'
          : `Confirmed on Zocdoc.${lagNote('confirm', result.observedStatus)}`,
    }
  }

  if (action === 'arrived' || action === 'no_show') {
    const result = await client.zocdocAppointmentStatus({ zocdocAppointmentId, status: action })
    if (result.status === 'refused' && result.refusal) {
      return { failure: `Not updated — ${result.refusal.detail}.` }
    }
    const marked = action === 'arrived' ? 'arrived' : 'a no-show'
    return {
      note:
        result.status === 'unchanged'
          ? `Zocdoc already recorded this as ${marked}, so nothing was sent.`
          : `Recorded as ${marked} on Zocdoc.${lagNote(action, result.observedStatus)}`,
    }
  }

  if (!reasonType) return { failure: 'Choose a reason before cancelling.' }
  const needsText = ZOCDOC_REASON_TYPES_NEEDING_TEXT.includes(reasonType)
  const result = await client.zocdocCancelAppointment({
    zocdocAppointmentId,
    reasonType,
    ...(needsText && reasonText.trim() && { reason: reasonText.trim() }),
  })
  if (result.status === 'refused' && result.refusal) {
    return { failure: `Not cancelled — ${result.refusal.detail}.` }
  }
  if (result.status === 'already-cancelled') {
    return { failure: 'Zocdoc already had this appointment cancelled, so nothing was sent.' }
  }
  // The one thing an operator must not miss: Zocdoc is cancelled, the doctor's calendar is not,
  // and only a person can decide to cancel a visit that may already carry billing.
  const lag = lagNote('cancel', result.observedStatus)

  // The Zocdoc side is done and cannot be undone, so from here nothing may throw the whole
  // action away: the beHuman leg reports beside it rather than replacing it.
  if (!alsoCancelBehuman) {
    return {
      note: result.behumanStillBooked
        ? `Cancelled on Zocdoc. The beHuman appointment is still on the practitioner’s calendar — cancel it there too if the visit is not happening.${lag}`
        : `Cancelled on Zocdoc.${lag}`,
    }
  }

  // Middleware's own identifier lookup is the authority on what to cancel — the row's copy was
  // read minutes ago, and cancelling the wrong encounter is not a recoverable mistake.
  if (!result.behumanEncounterId) {
    return {
      note: `Cancelled on Zocdoc. Nothing was cancelled in beHuman: no linked appointment was found for this booking at the moment of the write.${lag}`,
    }
  }

  try {
    const behuman = await cancelInBehuman(middleware, result.behumanEncounterId)
    return behuman.ok
      ? { note: `Cancelled on Zocdoc, and the beHuman appointment was cancelled too.${lag}` }
      : {
          note: `Cancelled on Zocdoc. The beHuman appointment could NOT be cancelled${
            behuman.message ? ` — ${behuman.message}` : ''
          }. It is still on the practitioner’s calendar.${lag}`,
        }
  } catch (cause) {
    return {
      note: `Cancelled on Zocdoc. The beHuman cancellation failed — ${
        cause instanceof Error ? cause.message : 'middleware could not be reached'
      }. It is still on the practitioner’s calendar.${lag}`,
    }
  }
}

/**
 * Zocdoc's reason codes as a person reads them. The values are Zocdoc's own — imported rather than
 * retyped, so the picker cannot drift from what the API accepts — and only the labels live here.
 */
const REASON_LABELS: Record<ZocdocCancellationReasonType, string> = {
  patient_no_longer_needs_appointment: 'Patient no longer needs the appointment',
  patient_no_longer_available: 'Patient is no longer available',
  other_patient_reason: 'Other patient reason…',
  missing_needed_patient_information: 'Missing patient information we need',
  payment_or_insurance_issue: 'Payment or insurance issue',
  patient_or_visit_type_not_accepted: 'Patient or visit type not accepted',
  provider_not_available: 'Provider is not available',
  rescheduling_patient: 'Rescheduling the patient',
  other_provider_reason: 'Other practice reason…',
}

/**
 * Which actions to offer from a status.
 *
 * Mirrored from what each Zocdoc endpoint accepts, and only to decide what is worth showing —
 * middleware re-checks every precondition against a fresh read, so this being momentarily stale
 * costs a readable refusal rather than a bad write. An unrecognised status offers everything and
 * lets middleware be the judge, which is the same posture the rest of this screen takes towards
 * Zocdoc's partly-unknown vocabulary.
 */
const actionsFor = (status: string) =>
  status in STATUS_CHIP || KNOWN_STATUSES.has(status)
    ? ACTIONS.filter((candidate) => candidate.from.includes(status))
    : ACTIONS

/** Statuses we know the vocabulary of, so an unknown one can be told apart from a handled one. */
const KNOWN_STATUSES = new Set([
  'pending_booking',
  'booking_failed',
  'confirmed',
  'pending_reschedule',
  'reschedule_failed',
  'rescheduled',
  'arrived',
  'no_show',
  'cancelled',
])

/**
 * The actions Zocdoc actually exposes for an appointment.
 *
 * **There is no "pending".** Zocdoc's `update-status` endpoint accepts `arrived` and `no_show` and
 * nothing else, and no endpoint moves an appointment *back* to a pending state — so offering it
 * would be offering a button that cannot exist. Confirm and Cancel are separate endpoints of their
 * own; Reschedule is the fourth and is not built yet.
 */
const ACTIONS = [
  {
    key: 'confirm' as const,
    label: 'Confirm',
    hint: 'Accept a pending booking. Zocdoc allows it only from “pending booking”.',
    /** Zocdoc confirms from this status alone. */
    from: ['pending_booking'],
  },
  {
    key: 'arrived' as const,
    label: 'Arrived',
    hint: 'Record that the patient showed up.',
    from: ['pending_booking', 'booking_failed', 'confirmed', 'pending_reschedule', 'rescheduled'],
  },
  {
    key: 'no_show' as const,
    label: 'No show',
    hint: 'Record that the patient did not show. Zocdoc accepts this within two days of the start.',
    from: ['pending_booking', 'booking_failed', 'confirmed', 'pending_reschedule', 'rescheduled'],
  },
  {
    key: 'cancel' as const,
    label: 'Cancel',
    hint: 'Cancel on Zocdoc. The patient gets Zocdoc’s own notice.',
    from: [
      'pending_booking',
      'booking_failed',
      'confirmed',
      'pending_reschedule',
      'reschedule_failed',
      'rescheduled',
    ],
  },
  {
    key: 'reschedule' as const,
    label: 'Reschedule',
    // The only action here that touches beHuman: leaving the visit at the old time would make the
    // practitioner's calendar actively wrong rather than merely stale.
    hint: 'Move the visit to another Zocdoc slot. The linked beHuman appointment moves with it.',
    from: ['pending_booking', 'confirmed', 'pending_reschedule', 'rescheduled'],
  },
]

type ActionKey = (typeof ACTIONS)[number]['key']
/**
 * The actions the confirm-and-send sheet handles itself.
 *
 * `reschedule` is deliberately outside this: it is not a one-shot confirmation but a two-step flow
 * with its own upstream read, its own state and its own outcome vocabulary, so it runs through
 * `RescheduleSteps` rather than `runAction`. Splitting the type is what keeps `EXPECTED_STATUS`
 * and `sendAction` from having to pretend they can handle it.
 */
type SheetActionKey = Exclude<ActionKey, 'reschedule'>

/** What the action sheet names before anything is sent. */
type ActionTarget = {
  zocdocAppointmentId: string
  patientName: string
  status: string
  when: string
  /**
   * The beHuman visit this booking is **bound to by identifier** — the row's own `synced` state,
   * which is that lookup's answer. Carried into the sheet so the cancel step can offer to cancel
   * both sides *before* anything is sent; middleware re-resolves it at write time and its answer
   * is the one acted on.
   */
  behumanEncounterId?: string
}

/** What the Import confirmation names before it writes anything. */
type ImportTarget = {
  zocdocAppointmentId: string
  patientName: string
  practitionerName: string
  when: string
  /**
   * What the practitioner's calendar holds at that instant, as the row already computed it.
   *
   * Carried here so the confirmation can offer to publish a slot *before* writing, rather than
   * letting the operator discover the refusal after pressing the button. Middleware re-checks it
   * at write time and its answer is the one that counts.
   */
  calendarState?: string
}

/**
 * The beHuman-status half of a row: whether the mapped practitioner's beHuman calendar
 * already holds this visit, and if not, whether there is a slot for it.
 *
 * Six states rather than a boolean, because the operator's next move differs for each: an
 * already-synced visit needs nothing, a free slot can be booked into, a time held by somebody
 * else is a conflict, and no slot at all means the practitioner published no availability there.
 */
const CALENDAR_CHIP: Record<string, { label: string; tone: string }> = {
  'slot-free': { label: 'slot free', tone: 'bg-[#DBEAFE] text-[#1D4ED8]' },
  'slot-claimed': { label: 'slot claimed', tone: 'bg-[#FEF3C7] text-[#B45309]' },
  'no-slot': { label: 'no slot', tone: 'bg-[#F1F2F4] text-[#6B7280]' },
}

function CalendarChip({
  calendar,
  href,
}: {
  calendar: NonNullable<Appointment['calendar']>
  /**
   * The bound visit's page in beHuman. Only ever given for `synced`, and only when the binding
   * carries an encounter id.
   *
   * `/call/:encounterId` — the visit page, carrying the SOAP note, billing and the virtual-visit
   * fields. Worth knowing that it is also the live call: mounting it allocates a Zoom client and
   * joins the session, so following this link from a future appointment puts the operator in that
   * visit rather than in a read-only view of it. Chosen deliberately as the appointment's page;
   * beHuman has no read-only per-appointment route.
   */
  href?: string
}) {
  if (calendar.state === 'synced') {
    // The only state that is a written fact rather than a comparison of start times, and the
    // only one a Zocdoc cancellation could safely act on.
    const title = calendar.appointmentId
      ? `Bound to beHuman appointment ${calendar.appointmentId}`
      : undefined
    const className =
      'behuman-v2-mulish inline-flex items-center gap-1 rounded-md bg-[#DCFCE7] px-1.5 py-0.5 text-[11px] font-bold tracking-[0.04em] text-[#15803D] uppercase'

    // A real anchor, like the practitioner cell's: middle-click, cmd-click and "copy link" all
    // work, and a new tab keeps the operator's place in this list.
    return href ? (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        title={title ? `${title} — open the visit` : 'Open this visit in beHuman'}
        className={cn([className, 'no-underline hover:bg-[#BBF7D0]'])}
      >
        <IconCheck size={12} />
        synced
        <IconExternalLink size={11} />
      </a>
    ) : (
      <span className={className} title={title}>
        <IconCheck size={12} />
        synced
      </span>
    )
  }

  if (calendar.state === 'booked') {
    // `samePatient` is only present when a chart is confirmed on both sides, so the three
    // renderings here are "this visit", "someone else's visit", and "a booking we cannot attribute".
    const tone =
      calendar.samePatient === true
        ? 'bg-[#DCFCE7] text-[#15803D]'
        : calendar.samePatient === false
          ? 'bg-[#FEE2E2] text-[#DC2626]'
          : 'bg-[#DBEAFE] text-[#1D4ED8]'
    const label =
      calendar.samePatient === true
        ? 'on calendar'
        : calendar.samePatient === false
          ? 'time taken'
          : 'on calendar'
    return (
      <span
        className={cn([
          'behuman-v2-mulish inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-bold tracking-[0.04em] uppercase',
          tone,
        ])}
        title={
          calendar.samePatient === false
            ? 'A different patient holds this time on the beHuman calendar'
            : calendar.appointmentId
              ? `beHuman appointment ${calendar.appointmentId}`
              : undefined
        }
      >
        {calendar.samePatient === true && <IconCheck size={12} />}
        {label}
      </span>
    )
  }

  const chip = CALENDAR_CHIP[calendar.state]
  if (!chip) return null
  return (
    <span
      className={cn([
        'behuman-v2-mulish inline-flex items-center rounded-md px-1.5 py-0.5 text-[11px] font-bold tracking-[0.04em] uppercase',
        chip.tone,
      ])}
    >
      {chip.label}
    </span>
  )
}

/**
 * Zocdoc's own appointments for a date window — `POST /v2/zocdoc/appointments/fetch`, wrapping
 * `GET /v1/appointments`. A read-through explorer: nothing is stored, and no patient is matched or
 * created.
 *
 * Two things about the window are worth knowing while reading a count here:
 *
 * - it is filtered by Zocdoc, not by this card, so `totalCount` is the size of the filtered set;
 * - Zocdoc filters on **UTC** while an appointment's `start_time` carries the provider's offset,
 *   so a visit late on the last day in a western zone can fall just outside.
 *
 * The first load covers the current calendar week, which is the window an operator checking on
 * today's bookings actually wants.
 *
 * `title` names the panel: the Zocdoc dashboard's own tab says "Appointments", while Schedule —
 * where the surrounding page is about beHuman's calendar — has to say whose appointments these are.
 *
 * `range`, when given, drives the window: Schedule passes the week its grid is on, so moving the
 * calendar moves this list with it. The From/To pickers still work — until the next change of
 * `range`, which wins.
 *
 * `refreshKey` and `onChanged` keep it in step with a calendar beside it: a new `refreshKey`
 * re-reads the current window (the calendar wrote something), and `onChanged` fires after a write
 * from here that the calendar shows — import, link, the action sheet, reschedule. Only writes cross
 * over, never plain loads, so neither side can set the other off in a loop.
 */
export function AppointmentsCard({
  title = 'Appointments',
  range,
  refreshKey,
  onChanged,
}: {
  title?: string
  range?: { fromDay: string; toDay: string }
  refreshKey?: number
  onChanged?: () => void
} = {}) {
  const client = useClient()
  // The legacy client, only for the calendar's own cancel endpoint — see `cancelInBehuman`.
  const middleware = useMiddlewareClient()
  const [{ fromDay, toDay }, setRange] = useState(() => range ?? thisWeek())
  /**
   * Zocdoc's own page index, **zero-based**, and the one thing on this card that is not derived
   * from the window.
   *
   * A page holds 25 (`ZOCDOC_APPOINTMENT_PAGE_SIZE`) and the default window is a whole week, so a
   * practice of any size pushes bookings onto a second page routinely. This card is not a report —
   * import, link, cancel, confirm and reschedule all happen from these rows — so a row that cannot
   * be reached is a booking that cannot be worked.
   */
  const [page, setPage] = useState(0)
  const [data, setData] = useState<AppointmentsResult | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  /**
   * The open row, held by id rather than as a copy of the row itself: confirming a link re-reads
   * the list, and a held copy would go on showing the pre-link `associatedPatients` until the
   * modal was closed and reopened.
   */
  const [detailsId, setDetailsId] = useState<string | null>(null)
  /** Which row is mid-write, so only its own button spins. */
  const [syncing, setSyncing] = useState<string | null>(null)
  const [syncFailure, setSyncFailure] = useState<string | null>(null)
  /**
   * The row awaiting an Import confirmation. Creating an Appointment and an Encounter is heavier
   * than writing an identifier, so it is named out loud before it happens.
   */
  const [importing, setImporting] = useState<ImportTarget | null>(null)
  /**
   * Ticked to publish a slot at the booking's time before importing.
   *
   * Belongs to the target it was ticked for, so opening a different row starts it unticked — it
   * commits a practitioner to a time they never offered, and carrying that across rows would do
   * it to the wrong doctor.
   */
  const [createSlotOnImport, setCreateSlotOnImport] = useState(false)
  /**
   * The row whose status was clicked, and which action was chosen for it — the modal's two steps.
   * `null` action means step one: the list of things Zocdoc will accept from this status.
   */
  const [acting, setActing] = useState<ActionTarget | null>(null)
  const [action, setAction] = useState<ActionKey | null>(null)
  const [reasonType, setReasonType] = useState<ZocdocCancellationReasonType | null>(null)
  const [reasonText, setReasonText] = useState('')
  /** Ticked by the operator when the bound beHuman visit should be cancelled too. */
  const [alsoCancelBehuman, setAlsoCancelBehuman] = useState(false)
  /** Said out loud after an action, because the beHuman visit is deliberately left standing. */
  const [actionNote, setActionNote] = useState<string | null>(null)
  /**
   * The row whose candidate charts are being judged, and the row a chart is being created for.
   *
   * Held here rather than inside each row: a modal per table row is twenty-five mounted dialogs
   * that fight over focus, and these already follow the pattern `detailsId` and `acting` set.
   */
  const [judging, setJudging] = useState<Appointment | null>(null)
  const [creatingChartFor, setCreatingChartFor] = useState<Appointment | null>(null)
  /** The chart the create form just made, and whether it came back bound to the booking. */
  const [chartCreated, setChartCreated] = useState<{ id: string; linkFailure?: string } | null>(
    null,
  )

  const load = useCallback(
    async (from: string, to: string, atPage: number) => {
      setLoading(true)
      setFailure(null)
      try {
        setData(await client.zocdocFetchAppointments({ fromDay: from, toDay: to, page: atPage }))
      } catch (cause) {
        setFailure(cause instanceof Error ? cause.message : 'Could not reach middleware')
      } finally {
        setLoading(false)
      }
    },
    [client],
  )

  // The card loads itself: unlike the patients explorer this is a single cheap page, and an ops
  // screen that shows nothing until clicked cannot answer "did that booking land?".
  // `refreshKey` is read by nothing inside: a new value is the whole signal.
  useEffect(() => {
    void load(fromDay, toDay, page)
  }, [load, fromDay, toDay, page, refreshKey])

  /** The re-read after a write the calendar beside this card also shows. */
  const reloadAfterWrite = () => {
    onChanged?.()
    return load(fromDay, toDay, page)
  }

  /**
   * The link for the chart the create form is about to make. Keyed on the row being created for,
   * so the identifier written is always that booking's — the hook rebuilds when the target moves.
   */
  const chartLink = useZocdocPatientLink(creatingChartFor?.participants?.patient?.patientId)

  /** A new window is a new list, so it starts at its own first page rather than the current one. */
  const setWindow = useCallback((next: { fromDay: string; toDay: string }) => {
    setRange(next)
    setPage(0)
  }, [])

  // Keyed on the two days rather than the object, so a parent re-rendering with an equal range
  // does not throw away an operator's own picks or their page.
  const rangeFrom = range?.fromDay
  const rangeTo = range?.toDay
  useEffect(() => {
    if (rangeFrom && rangeTo) setWindow({ fromDay: rangeFrom, toDay: rangeTo })
  }, [rangeFrom, rangeTo, setWindow])

  const appointments = data?.appointments ?? []
  const details = appointments.find((appointment) => appointment.id === detailsId) ?? null

  /**
   * Binds one Zocdoc booking to the beHuman visit it is.
   *
   * Middleware re-checks time, practitioner and patient against Zocdoc and FHIR before writing —
   * this screen's match was computed when the page loaded, and a wrong bind would put a later
   * cancellation on a stranger's calendar. So a refusal is an outcome to read out, not a crash.
   */
  /**
   * Creates the beHuman side of a Zocdoc booking.
   *
   * Every precondition is re-checked by middleware at the moment of the write — linked patient,
   * mapped practitioner, a free slot, a future start — so a refusal here is an outcome to read
   * out rather than a failure. Nothing is sent to the patient.
   */
  const importAppointment = async (target: ImportTarget) => {
    setSyncing(target.zocdocAppointmentId)
    setSyncFailure(null)
    try {
      const result = await client.zocdocImportAppointment({
        zocdocAppointmentId: target.zocdocAppointmentId,
        ...(createSlotOnImport && { createSlot: true }),
      })
      // The booking's insurance becomes a request somebody has to review, so the operator is told
      // where it went — or that it went nowhere and has to be entered by hand.
      const insuranceNote =
        result.insuranceDraft?.status === 'saved'
          ? ` Insurance (${result.insuranceDraft.payerName}) is waiting for review on the patient’s chart.`
          : result.insuranceDraft?.status === 'failed'
            ? ` The insurance on this booking could not be saved for review (${result.insuranceDraft.message ?? 'no reason given'}) — add it by hand.`
            : ''
      if (result.status === 'refused' && result.refusal) {
        setSyncFailure(`Not imported — ${result.refusal.detail}.`)
      } else if (result.status === 'cancelled-at-source') {
        // Not a refusal — nothing is wrong and nothing needs fixing — but the operator pressed
        // Import and has to be told why no visit appeared.
        setSyncFailure('Not imported — Zocdoc reports this booking as cancelled.')
      } else if (result.endMismatch) {
        setSyncFailure(
          `Imported. Note: ${result.endMismatch} — the slot’s time was used.${insuranceNote}`,
        )
      } else if (result.slotCreated) {
        // A published slot is availability the practitioner never offered, so it is reported
        // rather than left for them to find on their calendar.
        setSyncFailure(
          `Imported. A slot was published on ${target.practitionerName}’s calendar at ${target.when} to hold the visit.${insuranceNote}`,
        )
      } else if (insuranceNote) {
        setSyncFailure(`Imported.${insuranceNote}`)
      }
      setImporting(null)
      await reloadAfterWrite()
    } catch (cause) {
      setSyncFailure(cause instanceof Error ? cause.message : 'Could not import the appointment')
    } finally {
      setSyncing(null)
    }
  }

  /**
   * Runs the chosen action **on Zocdoc**. Our own calendar is untouched by all four: middleware
   * reports the bound beHuman appointment and this reads that back out, rather than hard-deleting
   * a visit as a side effect of a Zocdoc button.
   *
   * One function for all of them because the shape is identical — send, read the refusal or the
   * note, close, re-read the page — and only the call itself differs.
   */
  const runAction = async (target: ActionTarget) => {
    // `reschedule` has its own flow and never reaches here; the guard is what lets the rest of
    // this function keep the narrower key type.
    if (!action || action === 'reschedule') return
    setSyncing(target.zocdocAppointmentId)
    setSyncFailure(null)
    setActionNote(null)
    try {
      const outcome = await sendAction(
        client,
        middleware,
        action,
        target,
        reasonType,
        reasonText,
        alsoCancelBehuman,
      )
      if (outcome.failure) {
        setSyncFailure(outcome.failure)
        return
      }
      setActionNote(outcome.note ?? null)
      closeAction()
      await reloadAfterWrite()
    } catch (cause) {
      setSyncFailure(cause instanceof Error ? cause.message : 'Could not reach middleware')
    } finally {
      setSyncing(null)
    }
  }

  const closeAction = () => {
    setActing(null)
    setAction(null)
    setReasonType(null)
    setReasonText('')
    setAlsoCancelBehuman(false)
  }

  const syncAppointment = async (zocdocAppointmentId: string, appointmentId: string) => {
    setSyncing(zocdocAppointmentId)
    setSyncFailure(null)
    try {
      const result = await client.zocdocLinkAppointment({ zocdocAppointmentId, appointmentId })
      if (result.status === 'mismatch' && result.mismatch) {
        setSyncFailure(`Not linked — ${result.mismatch.detail}.`)
      } else if (result.status === 'conflict' && result.conflict) {
        setSyncFailure(
          result.conflict.kind === 'appointment-has-other-zocdoc'
            ? `That beHuman appointment is already linked to Zocdoc appointment ${result.conflict.existing}, so nothing was changed.`
            : `This Zocdoc appointment is already linked to beHuman appointment ${result.conflict.existing}, so nothing was changed.`,
        )
      }
      await reloadAfterWrite()
    } catch (cause) {
      setSyncFailure(cause instanceof Error ? cause.message : 'Could not link the appointment')
    } finally {
      setSyncing(null)
    }
  }

  return (
    <AdminPanel
      title={title}
      description="Zocdoc’s own appointments starting in the window. Read-through — nothing is stored."
      // The count sits beside the title rather than on its own line: the window it counts is
      // already spelled out by the From/To fields, so repeating it as prose said nothing new.
      badge={
        data?.ok && appointments.length > 0 ? (
          <>
            <span className="behuman-v2-mulish rounded-md bg-[#F1F2F4] px-2 py-0.5 text-[14px] font-bold text-[#4A5160]">
              {data.totalCount ?? appointments.length}
            </span>
            {/* The count is the whole filtered set; what is on screen is one page of it. Said as
                a range rather than as "more pages exist", which named a problem and offered no
                way out of it — the controls under the table are the way out. */}
            {(page > 0 || data.hasMore) && data.pageSize ? (
              <span className={HINT}>
                showing {page * data.pageSize + 1}–{page * data.pageSize + appointments.length}
              </span>
            ) : null}
          </>
        ) : null
      }
      actions={
        <>
          <USDateInput
            label="From"
            value={fromDay}
            onChange={(value) => value && setWindow({ fromDay: value, toDay })}
            w={132}
            classNames={FIELD_CLASSES}
          />
          <USDateInput
            label="To"
            value={toDay}
            onChange={(value) => value && setWindow({ fromDay, toDay: value })}
            w={132}
            classNames={FIELD_CLASSES}
          />
          <Button {...SECONDARY_BTN} onClick={() => setWindow(thisWeek())}>
            This week
          </Button>
          <Button
            {...PRIMARY_BTN}
            loading={loading}
            onClick={() => void load(fromDay, toDay, page)}
          >
            Refresh
          </Button>
        </>
      }
      footnote={
        <>
          The window is applied by Zocdoc in UTC, while an appointment’s start carries the
          provider’s own offset — a visit late on {toDay} in a western zone can fall just outside.
          {data?.startTimeUtcMin ? (
            <>
              {' '}
              Sent as <span className={ID_CHIP}>{data.startTimeUtcMin}</span> →{' '}
              <span className={ID_CHIP}>{data.startTimeUtcMax}</span>.
            </>
          ) : null}
        </>
      }
    >
      {/* Tinted while a page loads, as the Schedule grid is — the rows stay readable but cannot be
          acted on against a list that is about to change. Held open on the first load, when there
          is nothing yet for the overlay to cover. */}
      <div className={cn(['relative flex flex-col gap-3', loading && !data && 'min-h-[160px]'])}>
        <LoadingOverlay visible={loading} />
        {failure && (
          <Alert color="red" radius="md">
            {failure}
          </Alert>
        )}

        {data && !data.ok && (
          <Alert color="red" radius="md" title="Could not read appointments">
            <div className="flex flex-col gap-1">
              <span className="text-[13px]">{data.error}</span>
              {data.missingEnv?.length ? (
                <span className="text-[13px]">Unset: {data.missingEnv.join(', ')}</span>
              ) : null}
              {data.requestId && (
                <span className={HINT}>
                  Zocdoc request id <span className={ID_CHIP}>{data.requestId}</span>
                </span>
              )}
            </div>
          </Alert>
        )}

        {data?.ok && appointments.length === 0 && (
          <Alert color="blue" radius="md">
            No Zocdoc appointments start in this window. The read succeeded — an empty result is the
            expected state until a booking exists.
          </Alert>
        )}

        {data?.ok && appointments.length > 0 && (
          <>
            {/* Said out loud rather than left as a per-row gap: an unread patient looks exactly
                like an absent one in the table, and they mean different things. */}
            {data.participantsFailed ? (
              <Alert color="yellow" radius="md" title="Some patients could not be read">
                <span className="text-[13px]">
                  {data.participantsFailed} of {appointments.length} participant{' '}
                  {data.participantsFailed === 1 ? 'read' : 'reads'} failed. Those rows show no
                  patient — open the details to see why.
                </span>
              </Alert>
            ) : null}

            {/* A name that could not be read looks exactly like an unmapped provider in the
                table, so the difference is said out loud rather than left as a gap. */}
            {data.providerError ? (
              <Alert color="yellow" radius="md" title="Some practitioners could not be read">
                <span className="text-[13px]">{data.providerError}</span>
              </Alert>
            ) : null}

            {/* "Could not check" and "not on the calendar" are different answers, and only one of
                them is a reason to go looking. */}
            {data.calendarError ? (
              <Alert color="yellow" radius="md" title="The beHuman calendar could not be checked">
                <span className="text-[13px]">{data.calendarError}</span>
              </Alert>
            ) : null}

            {syncFailure ? (
              <Alert color="red" radius="md">
                {syncFailure}
              </Alert>
            ) : null}

            {/* Yellow rather than green when our side is still booked: the Zocdoc half succeeded,
                but the operator has one more thing to do and should not read it as "done". */}
            {actionNote ? (
              <Alert
                color={actionNote.includes('still on the practitioner') ? 'yellow' : 'blue'}
                radius="md"
              >
                {actionNote}
              </Alert>
            ) : null}

            {/* Yellow when the chart exists but is not bound to the booking: creating and inviting
                both succeeded, so this is not a failure — but there is one thing left to do, and a
                green banner would say the operator was finished. */}
            {chartCreated ? (
              <Alert
                color={chartCreated.linkFailure ? 'yellow' : 'green'}
                radius="md"
                title={
                  chartCreated.linkFailure
                    ? 'Patient created and invited, but not linked'
                    : 'Patient created, invited and linked'
                }
              >
                <span className="text-[13px]">
                  Chart <span className={ID_CHIP}>{chartCreated.id}</span> now exists in beHuman and
                  the invitation has been sent.{' '}
                  {chartCreated.linkFailure
                    ? `It does NOT carry the booking's Zocdoc patient id — ${chartCreated.linkFailure}. Open the chart chip on that row to link it once that is resolved.`
                    : 'It already carries the booking’s Zocdoc patient id, so nothing needs syncing.'}
                </span>
              </Alert>
            ) : null}

            <ScrollArea.Autosize mah={440} type="auto">
              <Table highlightOnHover verticalSpacing={8} fz="sm">
                <Table.Thead>
                  <Table.Tr className={TABLE_ROW}>
                    <Table.Th className={cn([TABLE_HEAD, 'w-[190px]'])}>Start</Table.Th>
                    <Table.Th className={cn([TABLE_HEAD, 'w-[200px]'])}>Practitioner</Table.Th>
                    <Table.Th className={TABLE_HEAD}>Patient</Table.Th>
                    <Table.Th className={cn([TABLE_HEAD, 'w-[110px]'])}>DOB</Table.Th>
                    {/* Sized for `pending_reschedule`, the longest value in Zocdoc's status
                        vocabulary, plus the caret — so the chip never wraps whatever arrives. */}
                    <Table.Th className={cn([TABLE_HEAD, 'w-[200px]'])}>Zocdoc status</Table.Th>
                    {/* The two status columns sit together on purpose: the point of this table is
                        comparing what Zocdoc says against what our own calendar holds. */}
                    <Table.Th className={cn([TABLE_HEAD, 'w-[190px]'])}>beHuman status</Table.Th>
                    <Table.Th className={cn([TABLE_HEAD, 'w-[70px]'])} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {appointments.map((appointment) => (
                    <AppointmentRow
                      key={appointment.id}
                      appointment={appointment}
                      onOpenDetails={() => setDetailsId(appointment.id)}
                      onJudgeCandidates={() => setJudging(appointment)}
                      onImportPatient={
                        canCreateChartFrom(appointment.participants?.patient)
                          ? () => {
                              setChartCreated(null)
                              setCreatingChartFor(appointment)
                            }
                          : undefined
                      }
                      syncing={syncing === appointment.id}
                      onImport={
                        // Offered on the strength of the two links alone — a mapped practitioner
                        // and (checked in the row) a confirmed chart. The slot is deliberately
                        // *not* part of the gate: whether the practitioner published availability
                        // at that instant is middleware's call at the moment of the write, and a
                        // refusal that says "publish availability first" tells the operator far
                        // more than a button that was never drawn. Already-synced rows are the one
                        // exclusion: that visit is on the calendar, so there is nothing to create.
                        appointment.provider?.practitionerId &&
                        appointment.calendar?.state !== 'synced'
                          ? (target) => {
                              setCreateSlotOnImport(false)
                              setImporting(target)
                            }
                          : undefined
                      }
                      onAct={
                        // Clickable when Zocdoc accepts at least one action from this status;
                        // otherwise there is nothing to offer and the label stays a label.
                        appointment.status && actionsFor(appointment.status).length > 0
                          ? (target) => {
                              setActionNote(null)
                              setSyncFailure(null)
                              setAction(null)
                              setActing(target)
                            }
                          : undefined
                      }
                      onSync={
                        appointment.calendar?.state === 'booked' &&
                        appointment.calendar.samePatient === true &&
                        appointment.calendar.appointmentId
                          ? () =>
                              void syncAppointment(
                                appointment.id,
                                appointment.calendar!.appointmentId!,
                              )
                          : undefined
                      }
                    />
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea.Autosize>

            {/* Drawn only when there is somewhere to go: on a single-page window the controls
                would be two permanently dead buttons under every list. */}
            {(page > 0 || data.hasMore) && (
              <Group justify="space-between" gap={8}>
                <span className={HINT}>
                  Page {page + 1}
                  {/* Zocdoc returns a count for the whole filtered set, not for this page, so the
                      page total is derived rather than reported — and only when the count is
                      there. `pageSize` is echoed back from the request. */}
                  {data.totalCount !== undefined && data.pageSize
                    ? ` of ${Math.ceil(data.totalCount / data.pageSize)}`
                    : ''}
                </span>
                <Group gap={8}>
                  <Button
                    {...SECONDARY_BTN}
                    disabled={page === 0 || loading}
                    onClick={() => setPage((current) => Math.max(0, current - 1))}
                  >
                    Previous
                  </Button>
                  <Button
                    {...SECONDARY_BTN}
                    // `MAX_ZOCDOC_PAGE` is Zocdoc's own ceiling, not ours: the contract refuses a
                    // higher index, so offering the button would only produce a validation error.
                    disabled={!data.hasMore || page >= MAX_ZOCDOC_PAGE || loading}
                    onClick={() => setPage((current) => current + 1)}
                  >
                    Next
                  </Button>
                </Group>
              </Group>
            )}
          </>
        )}
      </div>

      {/* Named before it happens: unlike Sync, this creates clinical resources on a real
          calendar, so the operator sees exactly who and when first. */}
      <Modal
        opened={importing !== null}
        onClose={() => {
          setImporting(null)
          setCreateSlotOnImport(false)
        }}
        radius={18}
        classNames={{
          content: MODAL_CONTENT,
          title: '!text-[17px] !font-extrabold !tracking-[-0.3px]',
        }}
        title="Import onto the beHuman calendar"
      >
        {importing && (
          <div className="flex flex-col gap-3">
            <p className="m-0 text-[13px] leading-relaxed text-[#1F2733]">
              This creates an appointment and an encounter for{' '}
              <span className="font-bold">{importing.patientName}</span> with{' '}
              <span className="font-bold">{importing.practitionerName}</span> at{' '}
              <span className="font-bold">{importing.when}</span>, claiming the practitioner’s free
              slot at that time.
              {importing.calendarState !== 'no-slot' &&
                ' It needs one: with no published availability at that instant the import is refused and nothing is written.'}
            </p>

            {/* Only the genuinely empty instant is repairable here. `slot-claimed` means an
                appointment already holds the time — a second slot on top would double-book the
                practitioner — and `no-schedule` has nothing for a Slot to hang off. */}
            {importing.calendarState === 'no-slot' && (
              <>
                <Checkbox
                  checked={createSlotOnImport}
                  onChange={(event) => setCreateSlotOnImport(event.currentTarget.checked)}
                  label="Publish a slot at this time"
                  description={`${importing.practitionerName} has published no availability at ${importing.when}. Ticking this creates a free slot on their calendar so the visit can be imported into it.`}
                  classNames={{ label: 'behuman-v2-mulish !text-[13px] !font-bold' }}
                />
                {!createSlotOnImport && (
                  <Alert color="yellow" radius="md">
                    <span className="text-[13px]">
                      Without a slot at that time the import will be refused, and nothing is
                      written.
                    </span>
                  </Alert>
                )}
              </>
            )}

            {importing.calendarState === 'slot-claimed' && (
              <Alert color="red" radius="md" title="That time is already taken">
                <span className="text-[13px]">
                  A slot exists at that time on {importing.practitionerName}’s calendar and is
                  already claimed, so the import will be refused. Publishing a second slot over it
                  would double-book them.
                </span>
              </Alert>
            )}
            <p className={cn([HINT, 'm-0'])}>
              Nothing is sent to the patient — they already have Zocdoc’s confirmation. The two
              bookings are linked by identifier, so a later change on either side can find the
              other.
            </p>
            <Group justify="flex-end" gap={8}>
              <Button
                {...SECONDARY_BTN}
                onClick={() => setImporting(null)}
                disabled={syncing !== null}
              >
                Cancel
              </Button>
              <Button
                {...PRIMARY_BTN}
                loading={syncing === importing.zocdocAppointmentId}
                disabled={importing.calendarState === 'no-slot' && !createSlotOnImport}
                onClick={() => void importAppointment(importing)}
              >
                {createSlotOnImport ? 'Publish the slot and import' : 'Create the appointment'}
              </Button>
            </Group>
          </div>
        )}
      </Modal>

      {/* Two steps in one modal: pick the action, then confirm it. One modal rather than a menu
          plus a dialog because the second step needs room — Cancel grows a reason and a note — and
          a popover that resizes under the cursor reads as a glitch. */}
      <Modal
        opened={acting !== null}
        onClose={closeAction}
        radius={18}
        classNames={{
          content: MODAL_CONTENT,
          title: '!text-[17px] !font-extrabold !tracking-[-0.3px]',
        }}
        title={action ? ACTIONS.find((a) => a.key === action)?.label : 'Change on Zocdoc'}
      >
        {acting && (
          <div className="flex flex-col gap-3">
            <p className="m-0 text-[13px] leading-relaxed text-[#1F2733]">
              <span className="font-bold">{acting.patientName}</span> ·{' '}
              {acting.when || 'no start reported'} · currently{' '}
              <span className="font-bold">
                {acting.status.replace(/_/g, ' ') || 'not reported'}
              </span>
            </p>

            {action === null ? (
              <>
                {/* Only what Zocdoc accepts from this status. Reschedule is the fourth action and
                    has no endpoint wired yet; "pending" is not offered because none exists. */}
                <div className="flex flex-col gap-1.5">
                  {actionsFor(acting.status).map((candidate) => (
                    <button
                      key={candidate.key}
                      type="button"
                      onClick={() => setAction(candidate.key)}
                      className={cn([
                        'flex cursor-pointer flex-col gap-0.5 rounded-xl border border-[#E7E9EE] bg-white px-3 py-2.5 text-left',
                        'hover:border-[#E5533D] hover:bg-[#FCF1EE]',
                        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E5533D]',
                      ])}
                    >
                      <span className="behuman-v2-mulish text-[14px] font-bold text-[#1F2733]">
                        {candidate.label}
                      </span>
                      <span className={HINT}>{candidate.hint}</span>
                    </button>
                  ))}
                </div>
                <p className={cn([HINT, 'm-0'])}>
                  Each of these changes the appointment on Zocdoc only. The beHuman calendar is
                  never written to from here.
                </p>
              </>
            ) : action === 'reschedule' ? (
              /* Its own flow, not a confirmation: a slot has to be read from Zocdoc and checked
                 against the beHuman calendar before there is anything to confirm. */
              <RescheduleSteps
                zocdocAppointmentId={acting.zocdocAppointmentId}
                patientName={acting.patientName}
                onBack={() => setAction(null)}
                onDone={(result) => {
                  setActionNote(rescheduleNote(result))
                  closeAction()
                  void reloadAfterWrite()
                }}
              />
            ) : (
              <>
                {action === 'cancel' && (
                  <>
                    <Select
                      label="Reason"
                      placeholder="Why is it being cancelled?"
                      classNames={FIELD_CLASSES}
                      data={ZOCDOC_CANCELLATION_REASON_TYPES.map((value) => ({
                        value,
                        label: REASON_LABELS[value],
                      }))}
                      value={reasonType}
                      onChange={(value) =>
                        setReasonType((value as ZocdocCancellationReasonType) || null)
                      }
                      searchable={false}
                      allowDeselect={false}
                    />
                    {/* Only for the two `other_*` codes: Zocdoc takes the free text with those and
                        refuses it with the rest, so the field appears exactly where it is accepted. */}
                    {reasonType && ZOCDOC_REASON_TYPES_NEEDING_TEXT.includes(reasonType) && (
                      <Textarea
                        label="Note"
                        placeholder="What should Zocdoc record as the reason?"
                        classNames={FIELD_CLASSES}
                        autosize
                        minRows={2}
                        maxLength={500}
                        value={reasonText}
                        onChange={(event) => setReasonText(event.currentTarget.value)}
                      />
                    )}
                  </>
                )}

                {/* Offered only for a booking bound to a beHuman visit by identifier, and only
                    for a cancellation: nothing else on this sheet has a beHuman counterpart. Left
                    unticked, because the beHuman cancellation hard-deletes the appointment and its
                    encounter — an opt-in, never a default. */}
                {action === 'cancel' && acting.behumanEncounterId && (
                  <Checkbox
                    checked={alsoCancelBehuman}
                    onChange={(event) => setAlsoCancelBehuman(event.currentTarget.checked)}
                    label="Cancel the beHuman appointment too"
                    description="Runs the calendar’s own cancellation: the slot reopens and the appointment and encounter are removed. Refused if the visit already carries a payment."
                    classNames={{ label: 'behuman-v2-mulish !text-[13px] !font-bold' }}
                  />
                )}

                <p className={cn([HINT, 'm-0'])}>
                  {action === 'cancel'
                    ? acting.behumanEncounterId
                      ? alsoCancelBehuman
                        ? 'Zocdoc notifies the patient. Both sides are cancelled — if the beHuman half is refused, this says so and the visit stays on the calendar.'
                        : 'Zocdoc notifies the patient. The linked beHuman appointment stays on the practitioner’s calendar unless you tick the box above.'
                      : 'Zocdoc notifies the patient. This booking is not linked to a beHuman appointment, so there is nothing on our calendar to cancel.'
                    : 'Zocdoc-side only — no beHuman resource changes.'}
                </p>

                <Group justify="space-between" gap={8}>
                  {/* Back rather than a second close: the operator chose an action a moment ago and
                      may well have meant the one under it. */}
                  <Button
                    {...SECONDARY_BTN}
                    onClick={() => setAction(null)}
                    disabled={syncing !== null}
                  >
                    Back
                  </Button>
                  <Group gap={8}>
                    <Button {...SECONDARY_BTN} onClick={closeAction} disabled={syncing !== null}>
                      Keep it
                    </Button>
                    <Button
                      {...PRIMARY_BTN}
                      loading={syncing === acting.zocdocAppointmentId}
                      disabled={
                        action === 'cancel' &&
                        (!reasonType ||
                          (ZOCDOC_REASON_TYPES_NEEDING_TEXT.includes(reasonType) &&
                            !reasonText.trim()))
                      }
                      onClick={() => void runAction(acting)}
                    >
                      {action === 'cancel'
                        ? alsoCancelBehuman
                          ? 'Cancel on both'
                          : 'Cancel the appointment'
                        : `Confirm ${ACTIONS.find((a) => a.key === action)?.label.toLowerCase()}`}
                    </Button>
                  </Group>
                </Group>
              </>
            )}
          </div>
        )}
      </Modal>

      <PatientCandidatesModal
        appointment={judging}
        onClose={() => setJudging(null)}
        // Closed on success: the question it was opened to answer has been answered, and the row
        // behind it is about to redraw as linked.
        onLinked={() => {
          setJudging(null)
          void load(fromDay, toDay, page)
        }}
      />

      {/* The app's own create-and-invite form, not a copy of it — so a chart made here is
          indistinguishable from one made in the patient list. Keyed by row because Mantine's
          `useForm` reads `initialValues` on mount only: a long-lived instance would keep the first
          booking's prefill and quietly offer the previous patient's details for the next one. */}
      {creatingChartFor?.participants?.patient && (
        <CreateUpdatePatientModal
          key={creatingChartFor.id}
          isOpen
          onClose={() => setCreatingChartFor(null)}
          prefill={prefillFromZocdoc(creatingChartFor.participants.patient)}
          // Bound to the booking the moment it exists, so the operator is never sent back to press
          // "Same patient" on the chart they just made. A failure is reported beside the creation
          // rather than as one: the chart exists and the invitation has gone out, and an operator
          // told only "could not link" would reasonably make a second chart.
          successCallback={(created) => {
            setCreatingChartFor(null)
            void chartLink.linkCreatedChart(created).then((result) => {
              setChartCreated({
                id: created.id ?? 'created',
                ...(result.detail && !result.linked && { linkFailure: result.detail }),
              })
              void load(fromDay, toDay, page)
            })
          }}
        />
      )}

      <AppointmentDetailsModal
        appointment={details}
        onClose={() => setDetailsId(null)}
        // A link or a new chart changes this row's `associatedPatients`, so the list is re-read
        // rather than left asserting "no chart matched" beside one that now exists. The open
        // modal reads the same row out of the list, so it updates with it.
        onImported={() => void load(fromDay, toDay, page)}
      />
    </AdminPanel>
  )
}

function AppointmentRow({
  appointment,
  onOpenDetails,
  onSync,
  onImport,
  onAct,
  onJudgeCandidates,
  onImportPatient,
  syncing,
}: {
  appointment: Appointment
  onOpenDetails: () => void
  /**
   * Present when Zocdoc would accept at least one action from this row's status. The status label
   * is the control — it is the thing the operator is already looking at when they decide what
   * happened to the visit, and a row of buttons would say the same thing four times.
   */
  onAct?: (target: ActionTarget) => void
  /**
   * Present only when all three match — same time, same practitioner, same patient. Anything
   * less is not offered here: middleware would refuse it, and offering a button that cannot
   * succeed is worse than offering none.
   */
  onSync?: () => void
  /**
   * Present when the Zocdoc provider is mapped to a beHuman practitioner. The row adds the second
   * half of the gate — the patient's chart must be confirmed — because an unlinked patient has to
   * be invited and confirmed by a person first, which is a deliberate rule rather than a missing
   * feature. The row shows the patient chip, so the reason is already on screen.
   *
   * Availability is not part of the gate; middleware re-checks the slot when the write happens
   * and names what is missing.
   */
  onImport?: (target: ImportTarget) => void
  /**
   * Open the candidate charts for judging. Always present — the cell decides whether to draw the
   * chip that calls it, and only a row that has candidates does.
   */
  onJudgeCandidates: () => void
  /**
   * Create and invite a chart for this booking's patient. Absent when Zocdoc gave no name or date
   * of birth, which is the create form's own minimum.
   */
  onImportPatient?: () => void
  syncing: boolean
}) {
  // The participants read is the authoritative patient; the list's embedded block is often absent
  // entirely, so it is only the fallback.
  const patient = appointment.participants?.patient ?? appointment.patient
  const name = [patient?.firstName, patient?.lastName].filter(Boolean).join(' ')
  const start = splitZocdocStart(appointment.startAt)
  /**
   * The one candidate a person has already confirmed, if any: its chart carries the Zocdoc
   * identifier written on confirmation, and that identifier is *this* appointment's patient.
   *
   * Read from `participants` alone rather than the embedded block, which carries no Zocdoc
   * patient id — with participants unread there are no candidates either, so the row falls
   * through to showing nothing.
   *
   * Matched by identifier rather than by there being exactly one candidate: a shared inbox puts
   * two charts on one email, and the confirmed one stays confirmed whichever way it sorts.
   */
  const zocdocPatientId = appointment.participants?.patient?.patientId
  const linked = zocdocPatientId
    ? appointment.associatedPatients.find((candidate) =>
        candidate.zocdocPatientIds.includes(zocdocPatientId),
      )
    : undefined
  const unconfirmed = appointment.associatedPatients.length - (linked ? 1 : 0)

  const provider = appointment.provider
  /**
   * Two names, and the cell says which it is showing: the beHuman doctor the booking lands on, or
   * Zocdoc's own name for a provider that maps to nobody — which is the state worth spotting
   * here, because such a booking has no calendar to go to.
   */
  const practitionerId = provider?.practitionerId

  return (
    <>
      {/* The visit reason lives here rather than in a column of its own: it is an opaque `pc_…`
          id that cost 170px and two wrapped lines to say nothing at a glance. Open details still
          shows it in full. */}
      <Table.Tr
        className={TABLE_ROW}
        {...(appointment.visitReasonId && {
          title: `Visit reason ${appointment.visitReasonId}`,
        })}
      >
        <Table.Td>
          {start ? (
            <div className="flex items-baseline gap-1.5 whitespace-nowrap">
              <span className="text-[13px] font-bold text-[#1F2733]">{start.time}</span>
              <span className={HINT}>{start.day}</span>
            </div>
          ) : (
            <span className={HINT}>—</span>
          )}
        </Table.Td>
        <Table.Td>
          {practitionerId ? (
            <>
              <div className="text-[13px] font-bold text-[#1F2733]">
                {provider?.practitionerName ?? practitionerId}
              </div>
              {/* Same link as the provider-links table: a new tab on this doctor's calendar,
                  which is the calendar this booking is destined for. */}
              <a
                href={`/schedule?practitioner=${practitionerId}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Open ${provider?.practitionerName ?? practitionerId}'s calendar in a new tab`}
                className="behuman-v2-mulish text-[12px] font-bold text-[#E5533D] no-underline hover:underline"
              >
                Calendar
              </a>
            </>
          ) : provider?.zocdocName || provider?.zocdocProviderId ? (
            <>
              <div className="text-[13px] text-[#1F2733]">
                {provider.zocdocName ?? provider.zocdocProviderId}
              </div>
              <span className="behuman-v2-mulish mt-0.5 inline-flex items-center rounded-md bg-[#FEF3C7] px-1.5 py-0.5 text-[11px] font-bold tracking-[0.04em] text-[#B45309] uppercase">
                unmapped
              </span>
            </>
          ) : (
            <span className={HINT}>no provider</span>
          )}
        </Table.Td>
        <Table.Td>
          {name ? (
            <div className="text-[13px] font-bold text-[#1F2733]">{name}</div>
          ) : (
            <span className={HINT}>no patient block</span>
          )}
          {/* The chart link belongs with the name: it is a statement about *this patient* —
              which beHuman chart the booking is bound to — not about the calendar write that
              the beHuman status column reports. */}
          <div className="mt-1">
            {/* Two different things, and the cell says which: a link somebody confirmed, or charts
                that merely look alike — the duplicate an operator must notice before booking
                again. */}
            {linked ? (
              <div className="flex flex-wrap items-center gap-1.5">
                {/* A real anchor, not an onClick: middle-click, cmd-click and "copy link" all
                    work, and a new tab keeps the operator's place in this list. */}
                <a
                  href={`/patients/${linked.patientId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Open the linked chart for ${linked.firstName ?? linked.patientId} in a new tab`}
                  title={`Chart ${linked.patientId} carries Zocdoc patient ${zocdocPatientId}`}
                  className="behuman-v2-mulish inline-flex items-center gap-1 rounded-md bg-[#DCFCE7] px-1.5 py-0.5 text-[11px] font-bold tracking-[0.04em] text-[#15803D] uppercase no-underline hover:bg-[#BBF7D0]"
                >
                  <IconCheck size={12} />
                  patient linked
                </a>
                {/* The link does not settle the others: a second chart on the same email is still
                    a possible duplicate. */}
                {unconfirmed > 0 && (
                  <span className={HINT}>
                    +{unconfirmed} other {unconfirmed === 1 ? 'chart' : 'charts'}
                  </span>
                )}
              </div>
            ) : appointment.associatedPatients.length > 0 ? (
              /* The same chip as before, now the way into judging them: it names the commonest
                 actionable state on this screen — a patient we already hold, unlinked — and used
                 to be a label that said so and offered nothing. */
              <button
                type="button"
                onClick={onJudgeCandidates}
                title="Review the charts that may be this patient"
                className="behuman-v2-mulish inline-flex cursor-pointer items-center rounded-md border-0 bg-[#FCF1EE] px-1.5 py-0.5 text-[11px] font-bold text-[#E5533D] hover:bg-[#FADFD9]"
              >
                {appointment.associatedPatients.length} beHuman chart
                {appointment.associatedPatients.length === 1 ? '' : 's'}
              </button>
            ) : appointment.associatedPatientsError ? (
              /* "Could not check" is not "we hold nobody". The search failed, so the empty list is
                 an absence of knowledge — offering Import patient here would create a duplicate of
                 the very chart a working search would have found. Same rule the details popup
                 applies, said in the space a row has. */
              <Tooltip
                label={`${appointment.associatedPatientsError}. Whether we already hold this patient is unknown — not answered "no".`}
                withArrow
                multiline
                w={280}
              >
                <span className="behuman-v2-mulish inline-flex items-center rounded-md bg-[#FEF3C7] px-1.5 py-0.5 text-[11px] font-bold tracking-[0.04em] text-[#B45309] uppercase">
                  chart check failed
                </span>
              </Tooltip>
            ) : /* Deliberately absent when candidates exist — a create button beside possible
                   matches is how duplicate charts get made. There the popup is the way in, with
                   the candidates on screen. */
            onImportPatient ? (
              // The button IS the statement that we hold no chart: a "no chart" chip beside
              // "Import patient" said the same thing twice.
              <Button {...SUBTLE_BTN} onClick={onImportPatient}>
                Import patient
              </Button>
            ) : (
              // No button to say it, so the chip still has to: Zocdoc gave no name or date of
              // birth, so there is nothing to create a chart from — and a cell left blank would
              // read as "not checked" rather than "we hold nobody".
              <span className="behuman-v2-mulish inline-flex items-center rounded-md bg-[#F1F2F4] px-1.5 py-0.5 text-[11px] font-bold tracking-[0.04em] text-[#6B7280] uppercase">
                no chart
              </span>
            )}
          </div>
        </Table.Td>
        <Table.Td>
          {patient?.dateOfBirth ? (
            <span className="text-[13px] whitespace-nowrap text-[#1F2733]">
              {formatDob(patient.dateOfBirth)}
            </span>
          ) : (
            <span className={HINT}>—</span>
          )}
        </Table.Td>
        <Table.Td>
          {appointment.status ? (
            onAct ? (
              // A real button, not a span with a click: it has to be reachable by keyboard and
              // announce itself, because it is the control that changes Zocdoc.
              <button
                type="button"
                onClick={() =>
                  onAct({
                    zocdocAppointmentId: appointment.id,
                    patientName: name || appointment.id,
                    status: appointment.status ?? '',
                    when: formatZocdocStart(appointment.startAt) ?? '',
                    // `synced` is the identifier match — a written binding, not a guess from
                    // matching start times, which is the only basis on which this screen may
                    // offer to cancel a real visit.
                    ...(appointment.calendar?.state === 'synced' &&
                      appointment.calendar.encounterId && {
                        behumanEncounterId: appointment.calendar.encounterId,
                      }),
                  })
                }
                title="Change this appointment on Zocdoc"
                className={cn([
                  'behuman-v2-mulish inline-flex cursor-pointer items-center gap-1 rounded-md border-0 px-2 py-0.5 text-[11px] font-bold tracking-[0.04em] whitespace-nowrap uppercase',
                  'hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E5533D]',
                  STATUS_CHIP[appointment.status] ?? 'bg-[#F1F2F4] text-[#6B7280]',
                ])}
              >
                {appointment.status.replace(/_/g, ' ')}
                <span aria-hidden className="text-[9px] leading-none opacity-60">
                  ▾
                </span>
              </button>
            ) : (
              <span
                className={cn([
                  'behuman-v2-mulish inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-bold tracking-[0.04em] whitespace-nowrap uppercase',
                  STATUS_CHIP[appointment.status] ?? 'bg-[#F1F2F4] text-[#6B7280]',
                ])}
              >
                {appointment.status.replace(/_/g, ' ')}
              </span>
            )
          ) : (
            // The published field list names no status property, so its absence is expected
            // rather than a data problem.
            <span className={HINT}>not reported</span>
          )}
        </Table.Td>
        <Table.Td>
          {/* The chip is absent rather than a state when the provider maps to nobody: there is no
              calendar to compare against, and the Practitioner column already says why. The
              actions are not tied to it — Import is offered on the two links alone. */}
          {(appointment.calendar || (onImport && linked)) && (
            <div className="flex flex-wrap items-center gap-1.5">
              {appointment.calendar && (
                <CalendarChip
                  calendar={appointment.calendar}
                  // The encounter, not the appointment: `/call/:encounterId` is the route, and the
                  // encounter is what the binding carries alongside the appointment id.
                  {...(appointment.calendar.encounterId && {
                    href: `/call/${appointment.calendar.encounterId}`,
                  })}
                />
              )}
              {onSync && (
                <Button {...SUBTLE_BTN} loading={syncing} onClick={onSync}>
                  Sync
                </Button>
              )}
              {onImport && linked && (
                <Button
                  {...SUBTLE_BTN}
                  loading={syncing}
                  onClick={() =>
                    onImport({
                      zocdocAppointmentId: appointment.id,
                      patientName: name || linked.patientId,
                      practitionerName:
                        appointment.provider?.practitionerName ??
                        appointment.provider?.practitionerId ??
                        'the mapped practitioner',
                      when: formatZocdocStart(appointment.startAt) ?? '',
                      ...(appointment.calendar && { calendarState: appointment.calendar.state }),
                    })
                  }
                >
                  Import
                </Button>
              )}
            </div>
          )}
        </Table.Td>
        <Table.Td>
          <Button {...SUBTLE_BTN} onClick={onOpenDetails}>
            Open details
          </Button>
        </Table.Td>
      </Table.Tr>
    </>
  )
}
